import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Building2, Users, BookOpen, ArrowLeftRight, Edit } from 'lucide-react';
import api from '../api/client';
import toast from 'react-hot-toast';
import Modal from '../components/ui/Modal';
import FormField, { Input, FormRow, SubmitBtn } from '../components/ui/FormField';

export default function BranchesPage() {
  const qc = useQueryClient();
  const [showNew, setShowNew] = useState(false);
  const [editBranch, setEditBranch] = useState<any>(null);
  const [form, setForm] = useState({ name:'', code:'', address:'', phone:'', email:'', manager:'' });
  const [saving, setSaving] = useState(false);

  const { data } = useQuery({ queryKey: ['branches'], queryFn: () => api.get('/branches') } as any);
  const branches = (data as any)?.data?.branches || [];

  const set = (k: string) => (e: any) => setForm(f => ({...f, [k]: e.target.value}));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name) return toast.error('სახელი სავალდებულოა');
    setSaving(true);
    try {
      if (editBranch) {
        await api.put(`/branches/${editBranch.id}`, form);
        toast.success('ფილიალი განახლდა!');
      } else {
        await api.post('/branches', form);
        toast.success('ფილიალი დაემატა!');
      }
      qc.invalidateQueries({ queryKey: ['branches'] });
      setShowNew(false); setEditBranch(null);
      setForm({ name:'', code:'', address:'', phone:'', email:'', manager:'' });
    } catch { toast.error('შეცდომა'); }
    finally { setSaving(false); }
  };

  const openEdit = (b: any) => {
    setForm({ name:b.name||'', code:b.code||'', address:b.address||'', phone:b.phone||'', email:b.email||'', manager:b.manager||'' });
    setEditBranch(b);
  };

  const STATS = [
    { icon: BookOpen, label: 'წიგნი', key: 'book_count', color: 'var(--accent)', bg: 'var(--accent-bg)' },
    { icon: Users, label: 'წევრი', key: 'member_count', color: 'var(--success)', bg: 'var(--success-bg)' },
    { icon: ArrowLeftRight, label: 'გაცემული', key: 'active_checkouts', color: 'var(--warning)', bg: 'var(--warning-bg)' },
  ];

  return (
    <div style={{padding:24,fontFamily:'system-ui,sans-serif'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:24}}>
        <div>
          <h1 style={{fontSize:20,fontWeight:600,color:'var(--text-primary)',margin:0}}>ფილიალები</h1>
          <p style={{fontSize:12,color:'var(--text-faint)',marginTop:4}}>ბიბლიოთეკის განყოფილებები და ფილიალები</p>
        </div>
        <button onClick={() => { setForm({ name:'', code:'', address:'', phone:'', email:'', manager:'' }); setEditBranch(null); setShowNew(true); }}
          style={{display:'flex',alignItems:'center',gap:6,padding:'8px 16px',background:'#1d4ed8',border:'none',borderRadius:8,color:'white',fontSize:12,fontWeight:500,cursor:'pointer'}}>
          <Plus size={15}/> ახალი ფილიალი
        </button>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(340px,1fr))',gap:16}}>
        {branches.map((b: any) => (
          <div key={b.id} style={{background:'var(--bg-card)',border:'1px solid #1e2d45',borderRadius:12,overflow:'hidden'}}>
            <div style={{padding:'14px 16px',borderBottom:'1px solid #1e2d45',display:'flex',alignItems:'center',gap:12}}>
              <div style={{width:40,height:40,borderRadius:10,background:'var(--bg-active)',border:'1px solid #2a3f6e',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                <Building2 size={20} style={{color:'var(--accent)'}} />
              </div>
              <div style={{flex:1}}>
                <div style={{fontSize:14,fontWeight:600,color:'var(--text-primary)'}}>{b.name}</div>
                {b.code && <div style={{fontSize:11,color:'var(--text-faint)',fontFamily:'monospace'}}>{b.code}</div>}
              </div>
              <button onClick={() => openEdit(b)}
                style={{background:'none',border:'1px solid #1e2d45',borderRadius:8,padding:'5px 8px',color:'var(--text-muted)',cursor:'pointer'}}>
                <Edit size={13} />
              </button>
            </div>

            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:0,borderBottom:'1px solid #1e2d45'}}>
              {STATS.map(st => (
                <div key={st.key} style={{padding:'12px 16px',textAlign:'center',borderRight:'1px solid #1e2d45'}}>
                  <div style={{fontSize:18,fontWeight:700,color:st.color}}>{Number(b[st.key]||0).toLocaleString()}</div>
                  <div style={{fontSize:10,color:'var(--text-faint)',marginTop:2}}>{st.label}</div>
                </div>
              ))}
            </div>

            <div style={{padding:'12px 16px'}}>
              {b.address && (
                <div style={{display:'flex',gap:6,marginBottom:6}}>
                  <i className="ti ti-map-pin" style={{color:'var(--text-dimmed)',fontSize:13,marginTop:1}} />
                  <span style={{fontSize:12,color:'var(--text-muted)'}}>{b.address}</span>
                </div>
              )}
              {b.phone && (
                <div style={{display:'flex',gap:6,marginBottom:6}}>
                  <i className="ti ti-phone" style={{color:'var(--text-dimmed)',fontSize:13,marginTop:1}} />
                  <span style={{fontSize:12,color:'var(--text-muted)'}}>{b.phone}</span>
                </div>
              )}
              {b.email && (
                <div style={{display:'flex',gap:6,marginBottom:6}}>
                  <i className="ti ti-mail" style={{color:'var(--text-dimmed)',fontSize:13,marginTop:1}} />
                  <span style={{fontSize:12,color:'var(--text-muted)'}}>{b.email}</span>
                </div>
              )}
              {b.manager && (
                <div style={{display:'flex',gap:6}}>
                  <i className="ti ti-user" style={{color:'var(--text-dimmed)',fontSize:13,marginTop:1}} />
                  <span style={{fontSize:12,color:'var(--text-muted)'}}>{b.manager}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <Modal open={showNew||!!editBranch} onClose={() => { setShowNew(false); setEditBranch(null); }}
        title={editBranch?'ფილიალის რედაქტირება':'ახალი ფილიალი'} width={500}>
        <form onSubmit={handleSubmit}>
          <FormRow>
            <FormField label="სახელი" required>
              <Input value={form.name} onChange={set('name')} placeholder="ფილიალის სახელი" />
            </FormField>
            <FormField label="კოდი">
              <Input value={form.code} onChange={set('code')} placeholder="MAIN" />
            </FormField>
          </FormRow>
          <FormField label="მისამართი">
            <Input value={form.address} onChange={set('address')} placeholder="ქალაქი, ქუჩა..." />
          </FormField>
          <FormRow>
            <FormField label="ტელეფონი">
              <Input value={form.phone} onChange={set('phone')} placeholder="+995..." />
            </FormField>
            <FormField label="ელ-ფოსტა">
              <Input type="email" value={form.email} onChange={set('email')} placeholder="branch@..." />
            </FormField>
          </FormRow>
          <FormField label="მენეჯერი">
            <Input value={form.manager} onChange={set('manager')} placeholder="სახელი გვარი" />
          </FormField>
          <SubmitBtn loading={saving} label={editBranch?'შენახვა':'დამატება'} />
        </form>
      </Modal>
    </div>
  );
}
